// 全局通用
import { supportServiceCountryList, countryList, nationalityList, prefixList, currencyList } from "./global/countries";
import { moneySourceList, understandWayList } from "./global/questionnaire";
import { applymentStatusList, orderStatusList, amendsStatusList, renewStatusList, refundStatusList } from "./global/status";
import {
  orderTypeList,
  applicantRoleTypeList,
  companyTypeList,
  companySuffixTypeList,
  stockList,
  investmentTypeList,
  fundTypeList,
  memberTypeList,
  positionHeldTypeList,
  genderTypeList,
} from "./global/type";
// 按国家区分
import { applyStepData } from "./byCountry/applyStep";
import { businessGenreData } from "./byCountry/businessGenre";
import { governmentData } from "./byCountry/government";
import { identificationData } from "./byCountry/identification";
import { invoiceLanguageData } from "./byCountry/invoiceLanguage";
import { provinceOrStateData } from "./byCountry/provinceOrState";
import { establishCauseData } from "./byCountry/questionnaire";
import { serviceCodeData } from "./byCountry/serviceCode";
import { tradeData } from "./byCountry/trade";
import { financialPeriodTypeData, entityTypeData, companyTypeData, raceTypeData } from "./byCountry/type";

const state = {
  // 全局通用
  supportServiceCountryList,
  countryList,
  nationalityList,
  prefixList,
  currencyList,
  moneySourceList,
  understandWayList,
  applymentStatusList,
  orderStatusList,
  amendsStatusList,
  renewStatusList,
  refundStatusList,
  orderTypeList,
  applicantRoleTypeList,
  companyTypeList,
  companySuffixTypeList,
  stockList,
  investmentTypeList,
  fundTypeList,
  memberTypeList,
  positionHeldTypeList,
  genderTypeList,
  // 按国家区分
  applyStepData,
  businessGenreData,
  governmentData,
  identificationData,
  invoiceLanguageData,
  provinceOrStateData,
  establishCauseData,
  serviceCodeData,
  tradeData,
  financialPeriodTypeData,
  entityTypeData,
  companyTypeData,
  raceTypeData,
};

const getters = {
  // 根据当前语言，返回对应选项列表(customLang为可选的自定义语言，传入时优先使用)
  globalFormatList: (state, getters, rootState) => (listName, customLang = "") => {
    const lang = customLang || rootState.language?.language || "zh";
    const list = state[listName] || [];
    return list.map(item => ({
      ...item,
      label: lang === "zh" ? item.zh : item.en,
      value: item.value,
    }));
  },
  // 根据当前语言和国家，返回对应选项列表
  byCountryFormatList:
    (state, getters, rootState) =>
    (listName, country = "") => {
      // 参数country为空时，使用申请单(vuex中state.applyment.applymentDetail.country)中的国家
      const countryValue = country || rootState.applyment?.applymentDetail?.country;
      const lang = rootState.language?.language || "zh";
      const rawList = state[listName]?.[countryValue] || [];
      // 定义递归格式化函数
      const formatItem = item => {
        const result = {
          ...item,
          label: lang === "zh" ? item.zh : item.en,
          value: item.value,
        };
        // 如果存在子级且是数组，递归处理
        if (Array.isArray(item.children) && item.children.length > 0) {
          result.children = item.children.map(child => formatItem(child));
        }
        return result;
      };
      // 执行映射转换
      return rawList.map(item => formatItem(item));
    },
  // 根据value，反推label(customLang为可选的自定义语言，传入时优先使用)
  getGlobalLabel: (state, getters) => (listName, value, customLang = "") => {
    const list = getters.globalFormatList(listName, customLang);
    const target = list.find(item => item.value === value);
    return target ? target.label : value;
  },
  // 根据value和国家，反推label
  getByCountryLabel:
    (state, getters) =>
    (listName, value, childrenValue, country = "") => {
      const list = getters.byCountryFormatList(listName, country);
      const target = list.find(item => item.value === value);
      if (childrenValue) {
        // 存在childrenValue参数，根据传入的childrenValue，反推childrenLabel
        const children = target.children || [];
        const childTarget = children.find(ele => ele.value === childrenValue);
        return childTarget ? childTarget.label : value;
      } else {
        // 不存在childrenValue参数，直接返回
        return target ? target.label : value;
      }
    },
  // 根据国家、业务类型、操作类型(后台业务人员才显示)，来获取对应的申请步骤
  getApplyStepList: (state, getters, rootState) => (customCountry, customBusinessType, customIsLaterService) => {
    const applymentDetail = rootState.applyment?.applymentDetail || {};
    // 参数customCountry为空时，使用申请单(vuex中state.applyment.applymentDetail.country)中的国家
    const country = customCountry || applymentDetail?.country;
    // 参数customBusinessType为空时，使用申请单(vuex中state.applyment.applymentDetail.businessType)中的服务类型
    const businessType = customBusinessType || applymentDetail?.businessType;
    // 参数customIsLaterService为空时，使用申请单(vuex中state.applyment.applymentDetail.isLaterService)中的是否为增加服务
    const isLaterService = typeof customIsLaterService === "boolean" ? customIsLaterService : applymentDetail?.isLaterService || false;
    // 返回对应申请步骤
    const list = state.applyStepData[country]?.[businessType] || [];
    // 是否为后台业务人员
    const isCoordinator = rootState.user?.isCoordinator;
    // 特殊处理：后台业务人员创建申请时，操作类型为"增加服务"，并且业务类型为"定制化服务"，需要跳过“KYC问卷、资料上传”步骤
    const customizedBusinessTypeList = ["COMPREHENSIVE_CUSTOMIZED", "FUND_ADMIN_CUSTOMIZED"];
    if (customizedBusinessTypeList.includes(businessType) && isLaterService && isCoordinator) {
      const listCopy = JSON.parse(JSON.stringify(list));
      const steps = listCopy
        .filter(item => item.idx >= 2)
        ?.map(ele => ({
          ...ele,
          idx: ele.idx - 2,
        }));
      if (steps[0]) steps[0].prevPath = null;
      return steps;
    } else {
      return list;
    }
  },
  // 根据国家和服务类型，来获取对应的服务code
  getServiceCodeMap: (state, getters, rootState) => (customCountry, customBusinessType) => {
    // 参数customCountry为空时，使用申请单(vuex中state.applyment.applymentDetail.country)中的国家
    const country = customCountry || rootState.applyment?.applymentDetail?.country;
    // 参数customBusinessType为空时，使用申请单(vuex中state.applyment.applymentDetail.businessType)中的服务类型
    const businessType = customBusinessType || rootState.applyment?.applymentDetail?.businessType;
    // 返回对应服务code映射对象
    const mapObj = state.serviceCodeData[country]?.[businessType] || {};
    return mapObj;
  },
  // 不区分国家和主体类型，获取所有的业务类型列表
  getAllBusinessTypeList: state => {
    const businessGenreData = state.businessGenreData;
    const uniqueChildren = Array.from(
      Object.values(businessGenreData)
        .flat() // 展开国家层级，得到所有的 category 对象
        .flatMap(item => item.children) // 展开所有 children
        .reduce((map, child) => {
          // 利用 Map 的 key 唯一性，按 value 进行去重
          if (!map.has(child.value)) {
            map.set(child.value, child);
          }
          return map;
        }, new Map())
        .values(), // 只取 Map 中的值
    );
    // 手动插入ADJUSTMENT(Quotation(V))业务类型
    uniqueChildren.push({
      value: "ADJUSTMENT",
      zh: "价格变更",
      en: "Invoice Variation",
    });
    return uniqueChildren;
  },
  // 根据当前语言和国家，返回行业最新的选项列表
  getTradeNewFormatList: (state, getters, rootState) => {
    // 直接使用申请单(vuex中state.applyment.applymentDetail.country)中的国家
    const countryValue = rootState.applyment?.applymentDetail?.country;
    const lang = rootState.language?.language || "zh";
    // 取首位最新的列表
    const list = state?.["tradeData"]?.[countryValue]?.[0] || [];
    return list.map(item => ({
      label: lang === "zh" ? item.zh : item.en,
      value: item.value,
    }));
  },
  // 根据value和国家，反推行业的label
  getTradeLabelByCountry: (state, getters, rootState) => value => {
    // 直接使用申请单(vuex中state.applyment.applymentDetail.country)中的国家
    const countryValue = rootState.applyment?.applymentDetail?.country;
    const lang = rootState.language?.language || "zh";
    // 取所有的行业列表(二维数组)
    const list = state?.["tradeData"]?.[countryValue] || [];
    // 将二维数组拉平
    const listFlat = list.flat();
    const current = listFlat.find(item => item.value == value);
    // 根据当前语言返回对应的label
    if (current) {
      return lang === "zh" ? current.zh : current.en;
    } else {
      return value;
    }
  },
  // 在发票中，根据传入的国家和语言缩写，得到对应的语言对象
  getInvoiceLanguageMap: state => (country, languageOption) => {
    if (!country || !languageOption || languageOption == "en") return {};
    const invoiceLanguageData = state.invoiceLanguageData || {};
    const mapObj = invoiceLanguageData[country]?.[languageOption] || {};
    return mapObj;
  },
  // 根据当前申请单国家，反推 componentName
  getCountryComponentName: (state, getters, rootState) => {
    const value = rootState.applyment?.applymentDetail?.country;
    const list = state.supportServiceCountryList || [];
    const target = list.find(item => item.value === value);
    return target ? target.componentName || "" : "";
  },
};

export default {
  namespaced: true,
  state,
  getters,
};
